import React, { useEffect } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import styled from 'styled-components';
import {
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Card,
  CardContent,
  TablePagination,
  IconButton,
  Collapse,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp';
import moment from 'moment-timezone';
import { uniqueId, isEmpty } from 'lodash';
import { translate } from 'containers/services/translate';
import messages from 'containers/LawyerDocumentsPage/messages';
import { COLORS } from 'constants/design';
import { ROW_PER_PAGE } from 'constants/global';
import { DOCUMENT_STATUS } from 'containers/ManagerDocumentsPage/constants';
import { openDocument } from 'containers/UploadDocuments/utils';

const Container = styled.div`
  margin-top: 20px;
  width: 100%;
  display: flex;
  flex-direction: column;
  .titleContainer {
    display: flex;
    align-items: center;
  }
  .documentsCount {
    padding: 5px;
    background: transparent
      linear-gradient(229deg, #707891 0%, #0b7cc5 0%, #56afbb 88%, #5db4ba 100%)
      0% 0% no-repeat padding-box;
    width: 46px;
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: ${COLORS.WHITE};
    font-weight: bold;
  }
  .title {
    padding: 5px;
    margin-left: 12px;
    font-family: Roboto;
    color: #384264;
    font-size: 18px;
    font-weight: bold;
  }
  .documents-container {
    width: 100%;
    padding: 10px;
    display: flex;
    flex-direction: column;
  }
  .required-document-title {
    font: 14px;
    color: #0075c9;
    font-weight: bold;
  }
  .docs {
    padding: 15px 0;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: flex-start;
    align-items: center;
  }
  .doc {
    margin: 5px;
    padding: 15px;
    width: 327px;
    height: 55px;
    background-color: #ffffff;
    box-shadow: 0px 1px 3px #0000001f;
    border-radius: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .doc-name {
    font-size: 14px;
    color: #2b2727;
    cursor: pointer;
  }
  .doc-title {
    display: flex;
    flex-direction: column;
  }
  .doc-type {
    font-size: 13px;
    color: #999999;
    text-transform: uppercase;
  }
  .cta {
    padding: 10px;
    display: flex;
    flex-direction: row;
    justify-content: space-between;
  }
`;

export interface RejectedDocumentsProps {
  intl: IntlShape;
  existantClaims: any;
  requestExistantClaims: Function;
  keyclokToken: string | undefined;
}

export const RejectedDocuments: React.FC<RejectedDocumentsProps> = (
  props,
): JSX.Element => {
  const [open, setOpen] = React.useState({} as { [index: string]: boolean });
  const [toggle, setToggle] = React.useState(false);

  const [page, setPage] = React.useState(0);
  const [pageSize, setRowsPerPage] = React.useState(25);

  useEffect(() => {
    if (props.keyclokToken)
      props.requestExistantClaims(DOCUMENT_STATUS.REJECTED);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.keyclokToken]);

  const tableHeaders = [
    messages.claimNumber,
    messages.claimNature,
    messages.policyNumber,
    messages.registrationNumber,
    messages.vehiculeBrand,
    messages.lossDate,
  ];

  const handleCollapseRow = (index: number) => (event: any) => {
    setToggle(toggle);
    setOpen({ [index]: !toggle });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    props.requestExistantClaims(DOCUMENT_STATUS.REJECTED, newPage, pageSize);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const size = parseInt(event.target.value, 10);
    setRowsPerPage(size);
    props.requestExistantClaims(DOCUMENT_STATUS.REJECTED, page, size);
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <Typography className="titleContainer">
            <span className="documentsCount">
              {props.existantClaims.length}
            </span>
            <span className="title">
              {translate(messages.rejectedDocumentsTitle, props)}
            </span>
          </Typography>
        </CardContent>
        <CardContent>
          <Table aria-label="collapsible table" className="claim-table">
            <TableHead>
              <TableRow className="claim-row-head">
                {tableHeaders.map(header => (
                  <TableCell key={uniqueId()}>
                    {translate(header, props)}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>
            {!isEmpty(props.existantClaims) &&
              props.existantClaims.map((claim: any, index: number) => (
                <TableBody className="table" key={claim.id}>
                  <TableRow>
                    <TableCell align="left">{claim.claimNumber}</TableCell>
                    <TableCell align="left">{claim.claimNature}</TableCell>
                    <TableCell align="left">{claim.policyNumber}</TableCell>
                    <TableCell align="left">
                      {claim.registrationNumber}
                    </TableCell>
                    <TableCell align="left">{claim.vehiculeBrand}</TableCell>
                    <TableCell align="left">
                      {`${moment(claim.lossDate).format('DD/MM/YYYY')} ${
                        claim.lossHour
                      }`}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        aria-label="expand row"
                        size="small"
                        onClick={handleCollapseRow(index)}
                      >
                        {open[index] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={9}
                      style={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        backgroundColor: '#f7f7f7',
                      }}
                    >
                      <Collapse in={open[index]} timeout="auto" unmountOnExit>
                        <div className="documents-container">
                          <div className="cta">
                            <span className="required-document-title">
                              {translate(
                                messages.requiredDocumentsTitle,
                                props,
                              )}
                            </span>
                          </div>
                          <div className="docs">
                            {claim?.documents.map(
                              (
                                document: {
                                  id: number;
                                  name: string;
                                  type: string;
                                  url: string;
                                },
                                documentIndex: number,
                              ) => (
                                <div className="doc" key={document.id}>
                                  <div
                                    className="doc-title"
                                    onClick={openDocument(document?.url)}
                                  >
                                    <span className="doc-name">
                                      {document.name}
                                    </span>
                                    <span className="doc-type">
                                      {document.type}
                                    </span>
                                  </div>
                                </div>
                              ),
                            )}
                          </div>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </TableBody>
              ))}
          </Table>
          <TablePagination
            rowsPerPageOptions={ROW_PER_PAGE}
            component="div"
            count={props.existantClaims.length}
            rowsPerPage={pageSize}
            page={page}
            labelRowsPerPage={translate({ ...messages.linesPerPage }, props)}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
          />
        </CardContent>
      </Card>
    </Container>
  );
};

export default injectIntl(RejectedDocuments);
